import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect } from "react";

/**
 * TerritoryPickerDialog - Dialog for selecting target territory
 * Shows territories with location counts to help user understand scope
 *
 * Props:
 * - isOpen: boolean - Whether dialog is open
 * - territories: Array - List of territory options with locationCount
 * - isLoading: boolean - Loading state
 * - onSelect: (territoryId) => void - Callback when territory selected
 * - onClose: () => void - Callback to close dialog
 */
const TerritoryPickerDialog = ({
  isOpen = false,
  territories = [],
  isLoading = false,
  onSelect,
  onClose,
}) => {
  const [selectedTerritoryId, setSelectedTerritoryId] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedTerritoryId("");
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedTerritoryId) {
      onSelect(selectedTerritoryId);
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Select Target Territory</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 120,
            }}
          >
            <CircularProgress />
          </Box>
        ) : territories.length === 0 ? (
          <Typography color="textSecondary">
            No territories available
          </Typography>
        ) : (
          <FormControl fullWidth>
            <InputLabel>Territory</InputLabel>
            <Select
              value={selectedTerritoryId}
              onChange={(e) => setSelectedTerritoryId(e.target.value)}
              label="Territory"
            >
              {territories.map((territory) => (
                <MenuItem
                  key={territory.id}
                  value={territory.id}
                >
                  <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
                    <span>{territory.name}</span>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      ({territory.locationCount} locations)
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedTerritoryId || isLoading}
        >
          Change Territory
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TerritoryPickerDialog;
